import React from 'react';
import { Row, Col, Card, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { formatDate } from '../utils/dataUtils';

const CategoryCards = ({ data, onCategoryClick }) => {
  if (!data || data.length === 0) {
    return (
      <div className="category-cards">
        <h4 className="mb-4">Categories Overview</h4>
        <div className="alert alert-info">No category data available</div>
      </div>
    );
  }

  // Get platform icon based on platform name
  const getPlatformIcon = (platform) => {
    const platformIcons = {
      'YouTube': 'fa-youtube',
      'Twitter': 'fa-twitter',
      'Instagram': 'fa-instagram',
      'Facebook': 'fa-facebook',
      'LinkedIn': 'fa-linkedin',
      'TikTok': 'fa-tiktok',
      'Pinterest': 'fa-pinterest',
      // Add more platform mappings as needed
    };
    
    return platformIcons[platform] || 'fa-globe'; // Default to globe icon if no match
  };

  // Helper to count how many brands are missing each platform
  const countMissingPlatforms = (missingAccounts) => {
    const platformCounts = {};
    
    missingAccounts.forEach(brand => {
      brand.missing_platforms.forEach(platform => {
        if (!platformCounts[platform]) {
          platformCounts[platform] = [];
        }
        platformCounts[platform].push(brand.brand_name);
      });
    });
    
    return platformCounts;
  };

  return (
    <div className="category-cards">
      <h4 className="mb-4">Categories Overview</h4>
      <Row>
        {data.map(category => (
          <Col md={6} lg={4} key={category.categoryid} className="mb-4">
            <Card 
              className={`category-card h-100 ${category.is_outdated ? 'border-danger' : 'border-success'}`}
              onClick={() => onCategoryClick(category)}
              style={{ cursor: 'pointer' }}
            >
              <Card.Header className={category.is_outdated ? 'bg-danger bg-opacity-10' : 'bg-success bg-opacity-10'}>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 me-2">
                      {category.category_name}
                      {category.is_outdated ? (
                        <i className="fas fa-exclamation-triangle text-danger ms-2" 
                          title="This category has outdated data"></i>
                      ) : (
                        <i className="fas fa-check-circle text-success ms-2"
                          title="Data is up to date"></i>
                      )}
                    </h5>
                    
                    {/* Missing Platform Icons in Header */}
                    {category.brands_missing_accounts && category.brands_missing_accounts.length > 0 && (
                      <div className="missing-platforms-header">
                        {Object.entries(countMissingPlatforms(category.brands_missing_accounts)).map(([platform, brands]) => (
                          <OverlayTrigger
                            key={platform}
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-${platform}-${category.categoryid}`}>
                                <strong>{platform}</strong> missing from:
                                <ul className="mb-0 ps-3">
                                  {brands.map((brand, idx) => (
                                    <li key={idx}>{brand}</li>
                                  ))}
                                </ul>
                              </Tooltip>
                            }
                          >
                            <div className="header-platform-icon">
                              <i className={`fab ${getPlatformIcon(platform)}`}></i>
                              {brands.length > 1 && <span className="header-count-indicator">{brands.length}</span>}
                            </div>
                          </OverlayTrigger>
                        ))}
                      </div>
                    )}
                  </div>
                  <Badge bg={category.is_outdated ? "danger" : "success"}>
                    {category.totalbrands} Brands
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body>
                <div className="category-stats mb-3">
                  <Row>
                    <Col xs={6}>
                      <small style={{ color: 'var(--text-primary)', fontWeight: 500, opacity: 0.9 }}>Total Accounts</small>
                      <h4>{category.totalaccounts}</h4>
                    </Col>
                    <Col xs={6}>
                      <small style={{ color: 'var(--text-primary)', fontWeight: 500, opacity: 0.9 }}>Total Posts</small>
                      <h4>{category.brandpost.toLocaleString()}</h4>
                    </Col>
                  </Row>
                </div>
                <div className="category-timeline mb-3">
                  <small className="d-block" style={{ color: 'var(--text-primary)', fontWeight: 500, opacity: 0.9 }}>Data Collection Period</small>
                  <div className="d-flex justify-content-between align-items-center">
                    <span>{formatDate(category.first_datafetchedinduration)}</span>
                    <i className="fas fa-arrow-right"></i>
                    <span>{formatDate(category.last_datafetchedinduration)}</span>
                  </div>
                </div>
                
                <div className="category-stats">
                  <Row>
                    <Col xs={12}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <small style={{ color: 'var(--text-primary)', fontWeight: 500, opacity: 0.9 }}>Comments</small>
                          <h5>{category.comments.toLocaleString()}</h5>
                        </div>
                        {category.is_outdated && (
                          <Badge bg="danger" pill>
                            Outdated Data
                          </Badge>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card.Body>
              <Card.Footer className={category.is_outdated ? "bg-danger bg-opacity-5" : "bg-success bg-opacity-5"}>
                <div className="d-flex justify-content-between align-items-center">
                  <small style={{ color: 'var(--text-primary)', fontWeight: 500, opacity: 0.9 }}>Last updated: {formatDate(category.last_datafetchedinduration)}</small>
                  
                  {category.brands_missing_accounts && category.brands_missing_accounts.length > 0 ? (
                    <Badge bg="warning" pill className="missing-accounts-badge" title="Some brands are missing accounts">
                      <i className="fas fa-exclamation-circle me-1"></i>
                      {category.brands_missing_accounts.length} {category.brands_missing_accounts.length === 1 ? 'brand' : 'brands'} missing accounts
                    </Badge>
                  ) : !category.is_outdated && (
                    <Badge bg="success" pill>
                      <i className="fas fa-sync-alt me-1"></i> Up to date
                    </Badge>
                  )}
                </div>
              </Card.Footer>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default CategoryCards;
